import {useMemo} from 'react';
import {IProductShort} from 'modules/products/types';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {TSnippetVersion} from 'core/types';

type TUseProductPrice = {
    product: IProductShort | IProduct;
    availability: 'IN_STOCK' | 'OUT_OF_STOCK' | 'FOR_RESERVATION';
    snippetVersion?: TSnippetVersion;
};

export const useProductPrice = ({
    product,
    availability,
    snippetVersion,
}: TUseProductPrice): IProductSnippet['price'] => {
    // eslint-disable-next-line complexity
    return useMemo(() => {
        const {price, qtyPerUOM} = product;
        const {specPrice, base, piece, discount, showDiscountPercent} = price;

        const isProductAvailable = !['OUT_OF_STOCK'].includes(availability);

        const current = price.isSpecPrice && specPrice ? specPrice : discount.price;
        const hasOld = isProductAvailable ? current < base.price : false;
        const old = hasOld ? base.price : null;
        const unit = 1 < qtyPerUOM && piece?.price ? piece.price : null;
        const percent = !showDiscountPercent || !discount.discountValue || !hasOld ? null : discount.discountValue;
        const currency = ((price.base.currency || price.discount.currency) ?? null) as 'RUB' | null;

        const isSpecialPrice = Boolean(current < base.price && price.isSpecPrice && specPrice);
        const isOnlineOnly = Boolean(product.labels?.find((label) => 'ONLINE_ONLY' === label.code));
        const isOnlineDiscount = price.isOnlineEcom;
        const hasUnitPrice = Boolean(unit);
        const cashback = isProductAvailable ? price.cashback || null : null;

        return {
            currency,
            current,
            percent,
            old,
            unit,
            cashback,
            badges: {
                isSpecialPrice,
                isOnlineDiscount,
                isSplitAvailable: false,
                ...('v2' === snippetVersion ? {isOnlineOnly} : {}),
                hasUnitPrice,
                isOutOfStock: 'OUT_OF_STOCK' === availability,
            },
        };
    }, [product, availability, snippetVersion]);
};
